import { Component } from "puzzle";
import * as _ from "lodash";
import {
  FILES_STATE,
  GS_BUILDUP_PROPS,
  LOADED_TARGET,
  MASS_SELECTION_CONFIGURATION,
  PRECONDITION_CHECK_RESPONSE_PARAMS,
  PROPS,
  TARGET
} from "../Types";
import GradingAndScoring from "./GradingAndScoring/GradingAndScoring";

export class PromoteDemoteAbstract<GENERIC_PROPS, GENERIC_STATE> extends Component <GENERIC_PROPS & PROPS, GENERIC_STATE & FILES_STATE> {
  warningFlags: any = {};
  promoteDemoteComment: string = '';

  /**
   * Resets warring flags object and commentField
   */
  clearWarringFlags = () => {
    this.warningFlags = {};
    this.promoteDemoteComment = '';
  };

  /**
   * Creates comments request object for saving comments on promote/demote
   * url: /submissions/{submissionId}/comments/add
   *
   * @param actionName
   * @param requestData
   */
  createCommentsRequestParams = (actionName: 'promote' | 'demote', requestData: any = {}) => {
    let filterData = !!_.get(requestData, 'targetIds') ? _.pick(requestData, 'targetIds') : requestData;
    let folder = _.get(this, 'props.urlConfigs.paramsConfig.folder');
    const commentRequestParams = {
      activeFilters: {
        ...(folder && { folder }),
        ...(filterData)
      },
      action: actionName === 'promote' ? 'COMPLETED' : 'DEMOTED',
      comment: this.getComment()
    };

    return commentRequestParams;
  };

  /**
   * Creates params for download, complete, return actions.
   *
   * @param selectAll
   * @param params
   */
  createRequestParams(selectAll: boolean, params = {}) {
    const {
      submissionData,
      urlConfigs
    } = this.props;
    const { mainParam } = urlConfigs.paramsConfig;
    const entityId: number | string = submissionData[mainParam];

    const {
      data,
      fileNameInputText,
      filters,
      headerSelection,
      massSelectionEnabled
    } = this.state;

    const massAction = massSelectionEnabled && (selectAll || headerSelection);
    if (massAction) {
      if (_.isEmpty(params)) {
        params = {
          ...params,
          ...filters,
          ...(fileNameInputText && { fileNameInputText }),
          [mainParam]: entityId
        }
      } else {
        params = {
          ...params,
          downloadPayloadCommand: {
            ...filters,
            ...(fileNameInputText && { fileNameInputText }),
            [mainParam]: entityId
          }
        }
      }
    } else {
      if (_.isEmpty(params)) {
        let phaseName = selectAll ? data[0]?.phaseName : (_.find(data, (item) => item.selected) as LOADED_TARGET)?.phaseName;
        if (phaseName) {
          params = {
            phaseName
          }
        }
      }

      params = {
        ...params,
        targetIds: (data as LOADED_TARGET[]).reduce((array: string[], item: LOADED_TARGET) => {
          const { selected, targetId, status } = item;
          if ((selectAll || selected) && status?.name !== 'CANCELLED') {
            array.push(targetId)
          }
          return array;
        }, []),
      };
    }

    return params;
  };

  /**
   * PromoteDemoteComment prop getter.
   */
  getComment = () => this.promoteDemoteComment;

  /**
   * DemotionPhase getter
   */
  getPreviousPhase = () => this.state.massSelectionConfiguration.demotionPhase;

  /**
   * Get localization used for dispatched dialog name and button
   * @param action
   */
  getLocalization = (action: 'promote' | 'demote') => {
    const key = 'promoteDemoteLocale';
    let locale = {
      btnLocale: this.translate(`${key}.${action}`),
      titleLocale: this.translate(`${key}.${action}Title`)
    };

    return locale;
  };

  /**
   * @override
   */
  protected getWarningFlags(action: 'promote' | 'demote', selectAll: boolean) {
    let warningFlagsConfiguration = {
      ...this.warningFlags
    };

    const flagSum = Object.keys(warningFlagsConfiguration).every((key: string) => !warningFlagsConfiguration[key]);
    warningFlagsConfiguration = {
      ...warningFlagsConfiguration,
      flagSum
    };
    return warningFlagsConfiguration;
  };


  /**
   * Phase 2 of validation.
   * If all action breaking conditions are met, precede to conditions that rise warnings/dialogs
   * @param action
   * @param selectAll
   */
  getWarningsFlagsForSelected = (action: 'promote' | 'demote', selectAll: boolean) => {
    const { data } = this.state;
    let promoteDemoteActiveFlags: { [key: string]: boolean } = {};
    let additionalFlag = ['gradingAndScoring', 'tempFile', 'workflowDefinitionId'];
    action === 'promote' ? additionalFlag.push('demoteCommentMandatoryEnabled') : additionalFlag.push('promoteCommentMandatoryEnabled');
    let filesWithNoChangesExist = false;
    let notCompletedFilesExist = false;
    let gradingAndScoring = false;
    let i = 0;
    let length = data.length;

    while (i < length && !(filesWithNoChangesExist && notCompletedFilesExist && gradingAndScoring)) {
      const { selected, status, targetId, workflowConfiguration } = data[i];
      if (selectAll || (selected && targetId)) {
        if (_.isEmpty(promoteDemoteActiveFlags)) {
          promoteDemoteActiveFlags = _.omit(workflowConfiguration, [...additionalFlag]);
        }

        const { name: statusName } = status;
        if (workflowConfiguration) {
          const { tempFile, gradingAndScoring: wfGradingAndScoringSetup } = workflowConfiguration;
          if (!filesWithNoChangesExist) {
            filesWithNoChangesExist = !(tempFile || (statusName === 'UPLOADED')); //true if file is unchanged, false otherwise
          }
          if (!notCompletedFilesExist) {
            notCompletedFilesExist = !!tempFile && (statusName !== 'UPLOADED');  //true if file has been saved but not completed online
          }

          if (!gradingAndScoring) {
            gradingAndScoring = wfGradingAndScoringSetup === 'requiredMandatory';  //g+s mandatory status
          }
        }
      }
      i++;
    }

    if (promoteDemoteActiveFlags.fileHasNoChangesEnabled) {
      promoteDemoteActiveFlags.fileHasNoChangesEnabled = filesWithNoChangesExist;
    }
    if (promoteDemoteActiveFlags.fileNotMarkedAsCompletedEnabled) {
      promoteDemoteActiveFlags.fileNotMarkedAsCompletedEnabled = notCompletedFilesExist;
    }
    if (!promoteDemoteActiveFlags['demoteCommentMandatoryEnabled'] && (action !== 'promote')) {
      promoteDemoteActiveFlags['generalReturnWarning'] = true;
    }

    promoteDemoteActiveFlags['gradingAndScoring'] = gradingAndScoring;

    return promoteDemoteActiveFlags;
  };

  /**
   * Grading and scoring action click handler
   * @param all
   * @param hiddeCompleteReturn
   * @param createdState
   * @param createdProps
   */
  gradeAndScore = ({ all }: { all: boolean }, createdState?: { data: TARGET, total: number } | undefined, createdProps?: GS_BUILDUP_PROPS | undefined) => {
    const gradingAndScoring = new GradingAndScoring({});
    gradingAndScoring.openGradingAndScoring(createdState || this.state, createdProps || this.props, all);
  };

  /**
   * Sets current flag value(key) to false so it will be ignore in next iteration
   * @param key
   * @param comment
   */
  setWarningFlags = (key: string | string[], comment: string = '') => {
    if (_.isArray(key)) {
      key.forEach((item: string) => {
        if (['promoteCommentMandatoryEnabled', 'demoteCommentMandatoryEnabled'].includes(item)) {
          if ('generalReturnWarning' in this.warningFlags) {
            this.warningFlags['generalReturnWarning'] = false;
          } else {
            this.warningFlags[item] = false;
            this.promoteDemoteComment = comment;
          }
        } else {
          this.warningFlags[item] = false;
        }
      });
    } else {
      if (['promoteCommentMandatoryEnabled', 'demoteCommentMandatoryEnabled'].includes(key)) {
        if ('generalReturnWarning' in this.warningFlags) {
          this.warningFlags['generalReturnWarning'] = false;
        } else {
          this.warningFlags[key] = false;
          this.promoteDemoteComment = comment;
        }
      } else {
        this.warningFlags[key] = false;
      }
    }
  };

  /**
   * Set mandatory comments flag,
   * also used for Grading and Scoring for checking is mandatory grade and score set on all targets.
   * @param action
   * @param responseData
   */
  setMandatoryCommentsFlag = (action: 'promote' | 'demote', responseData: PRECONDITION_CHECK_RESPONSE_PARAMS) => {
    let {
      mandatoryCommentPresent,
      mandatoryGradingAndScoringPresent
    } = responseData;

    if (action !== 'promote' && mandatoryCommentPresent) {
      this.warningFlags['generalReturnWarning'] = true;
    }

    let additionalFlag = action === 'promote' ? 'promoteCommentMandatoryEnabled' : 'demoteCommentMandatoryEnabled';
    if (this.warningFlags[additionalFlag]) {
      this.warningFlags[additionalFlag] = !mandatoryCommentPresent;
    }

    if (this.warningFlags['gradingAndScoring']) {
      this.warningFlags['gradingAndScoring'] = !mandatoryGradingAndScoringPresent;
    }
  }

  /**
   * Sets setMassActionConfiguration
   *
   * @param massActionConfiguration
   */
  setMassActionConfiguration = (massActionConfiguration: MASS_SELECTION_CONFIGURATION): void => {
    // @ts-ignore
    this.setState({
      massSelectionConfiguration: massActionConfiguration
    });
  };

  /**
   * Sets setFlagConfiguration flags are used for showing workflowConfiguration warnings
   *
   * @param action
   * @param configuration
   */
  setFlagConfiguration = (action: 'promote' | 'demote', configuration: any = null): void => {
    let massSelectionConfiguration = _.cloneDeep(configuration || this.state.massSelectionConfiguration);
    if (!massSelectionConfiguration.actionCannotBeRevertedEnabled && massSelectionConfiguration.promoteDemoteDisclaimerEnabled) {
      massSelectionConfiguration.promoteDemoteDisclaimerEnabled = false;
    }
    let requiredFlags = [
      'actionCannotBeRevertedEnabled',
      'fileHasNoChangesEnabled',
      'fileNotMarkedAsCompletedEnabled',
      'promoteDemoteDisclaimerEnabled',
      'gradingAndScoring'
    ];
    action !== 'promote' ? requiredFlags.push('demoteCommentMandatoryEnabled') : requiredFlags.push('promoteCommentMandatoryEnabled');
    this.warningFlags = _.pick(massSelectionConfiguration, [...requiredFlags]);
    if (this.warningFlags['gradingAndScoring']) {
      this.warningFlags['gradingAndScoring'] = this.warningFlags['gradingAndScoring'] === 'requiredMandatory';
    }

    if (action !== 'promote' && !massSelectionConfiguration.demoteCommentMandatoryEnabled) {
      this.warningFlags['generalReturnWarning'] = true;
    }
  };

  /**
   * Get mandatory comment and mandatory grading and scoring enabled flag
   * @param action
   * @param selectAll
   */
  getMandatoryCommentEnabled = (action: 'promote' | 'demote', selectAll: boolean) => {
    let {
      demoteCommentMandatoryEnabled,
      promoteCommentMandatoryEnabled,
      gradingAndScoring,
    } = this.getWarningFlags(action, selectAll);
    let mandatoryCommentEnabled = action === 'promote' ? promoteCommentMandatoryEnabled : demoteCommentMandatoryEnabled;

    return mandatoryCommentEnabled || gradingAndScoring;
  };

  /**
   * Update params for precondition check request.
   * Add phaseName to params if missing
   *
   * @param params
   * @param selectAll
   */
  updateParamsForPreconditionCheck = (params: any, selectAll: boolean) => {
    if (!params.phaseName) {
      let { data } = this.state;
      let phaseName = selectAll ? data[0]?.phaseName : (_.find(data, (item) => item.selected) as LOADED_TARGET)?.phaseName;
      params = {
        ...params,
        phaseName
      };
    }

    return params;
  }

  /**
   * Creates flag setup if GS has comment, to avoid showing promoteCommentMandatoryEnabled or demoteCommentMandatoryEnabled comment dialog
   * @param actionName
   */
  prepareGsFlags = (actionName: string) => ['gradingAndScoring', actionName === "promote" ? 'promoteCommentMandatoryEnabled' : 'demoteCommentMandatoryEnabled'];

}

